import React, { useEffect } from 'react'
import { X } from 'lucide-react'
import Layout from '../components/Layout'
import Pagination from '../components/Pagination'
import ImageCard from '../components/ImageCard'
import { useGazoKensaku } from '../hooks'
import { gazoKensakuService } from '../services/gazoKensakuService'

const GazoKensaku = () => {
  const {
    // Categories and tags
    satsueiMemoCategories,
    imageTagCategories,
    imageTags,

    // Search form state
    objectIds,
    setObjectIds,
    selectedImageTags,
    imageTagSearchType,
    setImageTagSearchType,
    imageTagSelector,
    setImageTagSelector,
    selectedSatsueiMemoCategory,
    selectedImageTagCategory,
    selectedImageTag,
    setSelectedImageTag,
    inputImageTag,
    setInputImageTag,

    isLoading,
    error,
    setError,

    // Pagination
    currentPage,
    itemsPerPage,
    totalCount,
    totalPages,
    pageStart,
    pageEnd,
    currentPageItems,

    // Functions
    handleSatsueiMemoCategoryChange,
    handleImageTagCategoryChange,
    addImageTag,
    removeImageTag,
    clearSearchConditions,
    performSearch,
    goToPage,
    goToFirstPage,
    goToLastPage,
    goToPreviousPage,
    goToNextPage,
    changeItemsPerPage
  } = useGazoKensaku()

  // Handle form submissions
  const handleSearch = () => {
    performSearch()
  }

  const handleAddSelectedTag = () => {
    if (selectedImageTag) {
      const tag = imageTags.find(t => t.imageTagId == selectedImageTag) // Use == for type coercion
      if (tag) {
        addImageTag(tag.imageTag)
      }
    }
  }

  const handleAddInputTag = () => {
    addImageTag(inputImageTag)
  }

  const handleClearConditions = () => {
    clearSearchConditions()
  }

  // Clear error after 3 seconds
  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(null), 3000)
      return () => clearTimeout(timer)
    }
  }, [error, setError])

  return (
    <Layout pageTitle="画像検索一覧">
      <div className="space-y-4">
        {/* Error message */}
        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
            {error}
          </div>
        )}

                 {/* Search Section */}
         <div className="bg-white border border-gray-300 p-4">
           {/* Desktop Layout */}
           <div className="hidden lg:flex">
             {/* Left side - Search form */}
             <div className="flex-1 mr-4">
               <div className="flex h-full">
                 {/* Object ID input */}
                 <textarea
                   id="desktop-object-ids"
                   data-testid="desktop-object-ids"
                   value={objectIds}
                   onChange={(e) => setObjectIds(e.target.value)}
                   placeholder="ID"
                   rows={1}
                   className="w-40 h-full border border-gray-300 p-2 text-sm font-bold resize-none"
                 />

                 {/* Image tag section */}
                 <div className="flex-1 ml-4">
                   <table className="w-full h-20">
                     <tbody>
                       <tr>
                         <th className="w-20 bg-gray-200 border border-gray-400 text-center text-xs py-2" rowSpan={2}>
                           画像タグ
                         </th>
                         <th className="w-24 bg-gray-200 border border-gray-400 text-center text-xs py-2">
                           指定方法
                         </th>
                         <td className="border border-gray-300 p-2">
                           <div className="flex justify-between items-start">
                             <div className="flex-1">
                               {/* Image tag selector dropdown */}
                               <select
                                 id="desktop-image-tag-selector"
                                 data-testid="desktop-image-tag-selector"
                                 value={imageTagSelector}
                                 onChange={(e) => setImageTagSelector(e.target.value)}
                                 className="border border-gray-300 p-1 text-xs bg-red-50 w-full mb-2"
                               >
                                 <option value="1">選択して追加</option>
                                 <option value="2">入力して追加</option>
                               </select>

                               {/* Selection method - below the selector dropdown */}
                               {imageTagSelector === '1' && (
                                 <div className="flex items-center space-x-2">
                                   <select
                                     id="desktop-satsuei-memo-category"
                                     data-testid="desktop-satsuei-memo-category"
                                     value={selectedSatsueiMemoCategory}
                                     onChange={(e) => handleSatsueiMemoCategoryChange(e.target.value)}
                                     className="border border-gray-300 p-1 text-xs"
                                   >
                                     <option value="">---</option>
                                     {(satsueiMemoCategories || []).map(category => (
                                       <option key={category.satsueiMemoCategoryId} value={category.satsueiMemoCategoryId}>
                                         {category.satsueiMemoCategoryName}
                                       </option>
                                     ))}
                                   </select>

                                   {selectedSatsueiMemoCategory && (
                                     <select
                                       value={selectedImageTagCategory}
                                       onChange={(e) => handleImageTagCategoryChange(e.target.value)}
                                       className="border border-gray-300 p-1 text-xs"
                                     >
                                       <option value="">---</option>
                                       {(imageTagCategories || []).map(category => (
                                         <option key={category.imageTagCategoryId} value={category.imageTagCategoryId}>
                                           {category.imageTagCategoryName}
                                         </option>
                                       ))}
                                     </select>
                                   )}

                                   {selectedImageTagCategory && (
                                     <>
                                       <select
                                         value={selectedImageTag}
                                         onChange={(e) => setSelectedImageTag(e.target.value)}
                                         className="border border-gray-300 p-1 text-xs"
                                       >
                                         <option value="">---</option>
                                         {(imageTags || []).map(tag => (
                                           <option key={tag.imageTagId} value={tag.imageTagId} data-imageTag={tag.imageTag}>
                                             {tag.imageTag}
                                           </option>
                                         ))}
                                       </select>
                                       {selectedImageTag && (
                                       <button
                                         id="desktop-add-selected-tag"
                                         data-testid="desktop-add-selected-tag"
                                         onClick={handleAddSelectedTag}
                                         className="bg-gray-500 text-white px-2 py-1 text-xs hover:bg-gray-600"
                                       >
                                         追加
                                       </button>
                                       )}
                                     </>
                                   )}
                                 </div>
                               )}

                               {/* Input method */}
                               {imageTagSelector === '2' && (
                                 <div className="flex items-center space-x-2">
                                   <input
                                     id="desktop-input-image-tag"
                                     data-testid="desktop-input-image-tag"
                                     type="text"
                                     value={inputImageTag}
                                     onChange={(e) => setInputImageTag(e.target.value)}
                                     className="border border-gray-300 p-1 text-xs"
                                     placeholder="タグを入力"
                                   />
                                   <button
                                     id="desktop-add-input-tag"
                                     data-testid="desktop-add-input-tag"
                                     onClick={handleAddInputTag}
                                     className="bg-gray-500 text-white px-2 py-1 text-xs hover:bg-gray-600"
                                   >
                                     追加
                                   </button>
                                 </div>
                               )}
                             </div>

                             <select
                               id="desktop-image-tag-search-type"
                               data-testid="desktop-image-tag-search-type"
                               value={imageTagSearchType}
                               onChange={(e) => setImageTagSearchType(e.target.value)}
                               className="border border-gray-300 p-1 text-xs w-48 ml-2"
                             >
                               <option value="0">指定タグいずれかが設定の画像</option>
                               <option value="1">指定タグ全て設定の画像</option>
                             </select>
                           </div>
                         </td>
                       </tr>
                       <tr>
                         <td colSpan={2} className="border border-gray-300 p-2 h-full">
                           {/* Selected tags display - proper height to show full tags */}
                           <div className="flex flex-wrap gap-2 min-h-[24px]" data-testid="desktop-selected-tags">
                             {(selectedImageTags || [])
                               .filter(tag => !tag.deleteFlg)
                               .map((tag, index) => (
                                 <div
                                   key={`${tag.imageTag}-${index}`}
                                   className="bg-gray-200 border border-gray-400 inline-flex items-center text-xs px-2 py-1"
                                   data-testid={`desktop-tag-${tag.imageTag}`}
                                 >
                                   {tag.imageTag}
                                   <button
                                     data-testid={`desktop-remove-tag-${tag.imageTag}`}
                                     onClick={() => removeImageTag(tag.imageTag)}
                                     className="ml-2 text-gray-600 hover:text-gray-800"
                                   >
                                     <X className="w-3 h-3" />
                                   </button>
                                 </div>
                               ))}
                           </div>
                         </td>
                       </tr>
                     </tbody>
                   </table>
                 </div>
               </div>
             </div>

             {/* Right side - Search buttons */}
             <div className="w-32 h-20 flex flex-col justify-between gap-2">
               <button
                 id="desktop-search-button"
                 data-testid="desktop-search-button"
                 onClick={handleSearch}
                 disabled={isLoading}
                 className="w-full py-3 px-5 bg-red-500 text-white text-lg font-bold hover:bg-red-600 disabled:opacity-50 disabled:cursor-not-allowed"
               >
                 {isLoading ? '検索中...' : '検索'}
               </button>
               <button
                 id="desktop-clear-button"
                 data-testid="desktop-clear-button"
                 onClick={handleClearConditions}
                 className="w-full py-2 px-2 text-xs text-gray-500 border border-gray-400 bg-gray-100 hover:bg-gray-200 leading-tight"
               >
                 検索条件を<br />クリアする
               </button>
             </div>
           </div>

           {/* Mobile Layout */}
           <div className="md:hidden space-y-4">
             {/* Object ID input */}
             <div>
               <label htmlFor="mobile-object-ids" className="block text-sm font-medium text-gray-700 mb-1">ID</label>
               <textarea
                 id="mobile-object-ids"
                 data-testid="mobile-object-ids"
                 value={objectIds}
                 onChange={(e) => setObjectIds(e.target.value)}
                 placeholder="ID"
                 rows={3}
                 className="w-full border border-gray-300 p-2 text-sm font-bold resize-none rounded"
               />
             </div>

             {/* Image tag section */}
             <div className="space-y-3">
               <div className="bg-gray-100 p-3 rounded">
                 <h3 className="text-sm font-medium text-gray-700 mb-2">画像タグ</h3>
                 
                 {/* Image tag selector dropdown */}
                 <div className="mb-3">
                   <label htmlFor="mobile-image-tag-selector" className="block text-xs text-gray-600 mb-1">指定方法</label>
                   <select
                     id="mobile-image-tag-selector"
                     data-testid="mobile-image-tag-selector"
                     value={imageTagSelector}
                     onChange={(e) => setImageTagSelector(e.target.value)}
                     className="w-full border border-gray-300 p-2 text-sm bg-red-50 rounded"
                   >
                     <option value="1">選択して追加</option>
                     <option value="2">入力して追加</option>
                   </select>
                 </div>

                 {/* Selection method */}
                 {imageTagSelector === '1' && (
                   <div className="space-y-2">
                     <div>
                       <label htmlFor="mobile-satsuei-memo-category" className="block text-xs text-gray-600 mb-1">カテゴリ</label>
                       <select
                         id="mobile-satsuei-memo-category"
                         data-testid="mobile-satsuei-memo-category"
                         value={selectedSatsueiMemoCategory}
                         onChange={(e) => handleSatsueiMemoCategoryChange(e.target.value)}
                         className="w-full border border-gray-300 p-2 text-sm rounded"
                       >
                         <option value="">---</option>
                         {(satsueiMemoCategories || []).map(category => (
                           <option key={category.satsueiMemoCategoryId} value={category.satsueiMemoCategoryId}>
                             {category.satsueiMemoCategoryName}
                           </option>
                         ))}
                       </select>
                     </div>

                     {selectedSatsueiMemoCategory && (
                       <div>
                         <label className="block text-xs text-gray-600 mb-1">サブカテゴリ</label>
                         <select
                           value={selectedImageTagCategory}
                           onChange={(e) => handleImageTagCategoryChange(e.target.value)}
                           className="w-full border border-gray-300 p-2 text-sm rounded"
                         >
                           <option value="">---</option>
                           {(imageTagCategories || []).map(category => (
                             <option key={category.imageTagCategoryId} value={category.imageTagCategoryId}>
                               {category.imageTagCategoryName}
                             </option>
                           ))}
                         </select>
                       </div>
                     )}

                     {selectedImageTagCategory && (
                       <div>
                         <label className="block text-xs text-gray-600 mb-1">タグ</label>
                         <div className="flex space-x-2">
                           <select
                             value={selectedImageTag}
                             onChange={(e) => setSelectedImageTag(e.target.value)}
                             className="flex-1 border border-gray-300 p-2 text-sm rounded"
                           >
                             <option value="">---</option>
                             {(imageTags || []).map(tag => (
                               <option key={tag.imageTagId} value={tag.imageTagId} data-imageTag={tag.imageTag}>
                                 {tag.imageTag}
                               </option>
                             ))}
                           </select>
                           {selectedImageTag && (
                             <button
                               onClick={handleAddSelectedTag}
                               className="bg-gray-500 text-white px-4 py-2 text-sm hover:bg-gray-600 rounded"
                             >
                               追加
                             </button>
                           )}
                         </div>
                       </div>
                     )}
                   </div>
                 )}

                 {/* Input method */}
                 {imageTagSelector === '2' && (
                   <div>
                     <label className="block text-xs text-gray-600 mb-1">タグを入力</label>
                     <div className="flex space-x-2">
                       <input
                         type="text"
                         value={inputImageTag}
                         onChange={(e) => setInputImageTag(e.target.value)}
                         className="flex-1 border border-gray-300 p-2 text-sm rounded"
                         placeholder="タグを入力"
                       />
                       <button
                         onClick={handleAddInputTag}
                         className="bg-gray-500 text-white px-4 py-2 text-sm hover:bg-gray-600 rounded"
                       >
                         追加
                       </button>
                     </div>
                   </div>
                 )}

                 {/* Search type dropdown */}
                 <div className="mt-3">
                   <label className="block text-xs text-gray-600 mb-1">検索条件</label>
                   <select
                     value={imageTagSearchType}
                     onChange={(e) => setImageTagSearchType(e.target.value)}
                     className="w-full border border-gray-300 p-2 text-sm rounded"
                   >
                     <option value="0">指定タグいずれかが設定の画像</option>
                     <option value="1">指定タグ全て設定の画像</option>
                   </select>
                 </div>

                 {/* Selected tags display */}
                 <div className="mt-3">
                   <label className="block text-xs text-gray-600 mb-1">選択されたタグ</label>
                   <div className="flex flex-wrap gap-2 min-h-[32px] p-2 border border-gray-300 rounded bg-white">
                     {(selectedImageTags || [])
                       .filter(tag => !tag.deleteFlg)
                       .map((tag, index) => (
                         <div
                           key={`${tag.imageTag}-${index}`}
                           className="bg-gray-200 border border-gray-400 inline-flex items-center text-xs px-2 py-1 rounded"
                         >
                           {tag.imageTag}
                           <button
                             onClick={() => removeImageTag(tag.imageTag)}
                             className="ml-2 text-gray-600 hover:text-gray-800"
                           >
                             <X className="w-3 h-3" />
                           </button>
                         </div>
                       ))}
                   </div>
                 </div>
               </div>
             </div>

             {/* Mobile Search buttons */}
             <div className="flex space-x-2">
               <button
                 id="mobile-search-button"
                 data-testid="mobile-search-button"
                 onClick={handleSearch}
                 disabled={isLoading}
                 className="flex-1 py-3 px-4 bg-red-500 text-white text-lg font-bold hover:bg-red-600 disabled:opacity-50 disabled:cursor-not-allowed rounded"
               >
                 {isLoading ? '検索中...' : '検索'}
               </button>
               <button
                 id="mobile-clear-button"
                 data-testid="mobile-clear-button"
                 onClick={handleClearConditions}
                 className="py-3 px-4 text-sm text-gray-500 border border-gray-400 bg-gray-100 hover:bg-gray-200 rounded"
               >
                 クリア
               </button>
             </div>
           </div>

           {/* Tablet Layout (iPad) */}
           <div className="hidden md:block lg:hidden space-y-4">
             {/* Object ID input */}
             <div>
               <label className="block text-sm font-medium text-gray-700 mb-1">ID</label>
               <textarea
                 value={objectIds}
                 onChange={(e) => setObjectIds(e.target.value)}
                 placeholder="ID"
                 rows={2}
                 className="w-full border border-gray-300 p-2 text-sm font-bold resize-none rounded"
               />
             </div>

             {/* Image tag section */}
             <div className="space-y-3">
               <div className="bg-gray-100 p-4 rounded">
                 <h3 className="text-sm font-medium text-gray-700 mb-3">画像タグ</h3>
                 
                 {/* Image tag selector dropdown */}
                 <div className="mb-4">
                   <label className="block text-xs text-gray-600 mb-1">指定方法</label>
                   <select
                     value={imageTagSelector}
                     onChange={(e) => setImageTagSelector(e.target.value)}
                     className="w-full border border-gray-300 p-2 text-sm bg-red-50 rounded"
                   >
                     <option value="1">選択して追加</option>
                     <option value="2">入力して追加</option>
                   </select>
                 </div>

                 {/* Selection method */}
                 {imageTagSelector === '1' && (
                   <div className="space-y-3">
                     <div>
                       <label className="block text-xs text-gray-600 mb-1">カテゴリ</label>
                       <select
                         value={selectedSatsueiMemoCategory}
                         onChange={(e) => handleSatsueiMemoCategoryChange(e.target.value)}
                         className="w-full border border-gray-300 p-2 text-sm rounded"
                       >
                         <option value="">---</option>
                         {(satsueiMemoCategories || []).map(category => (
                           <option key={category.satsueiMemoCategoryId} value={category.satsueiMemoCategoryId}>
                             {category.satsueiMemoCategoryName}
                           </option>
                         ))}
                       </select>
                     </div>

                     {selectedSatsueiMemoCategory && (
                       <div>
                         <label className="block text-xs text-gray-600 mb-1">サブカテゴリ</label>
                         <select
                           value={selectedImageTagCategory}
                           onChange={(e) => handleImageTagCategoryChange(e.target.value)}
                           className="w-full border border-gray-300 p-2 text-sm rounded"
                         >
                           <option value="">---</option>
                           {(imageTagCategories || []).map(category => (
                             <option key={category.imageTagCategoryId} value={category.imageTagCategoryId}>
                               {category.imageTagCategoryName}
                             </option>
                           ))}
                         </select>
                       </div>
                     )}

                     {selectedImageTagCategory && (
                       <div>
                         <label className="block text-xs text-gray-600 mb-1">タグ</label>
                         <div className="flex space-x-2">
                           <select
                             value={selectedImageTag}
                             onChange={(e) => setSelectedImageTag(e.target.value)}
                             className="flex-1 border border-gray-300 p-2 text-sm rounded"
                           >
                             <option value="">---</option>
                             {(imageTags || []).map(tag => (
                               <option key={tag.imageTagId} value={tag.imageTagId} data-imageTag={tag.imageTag}>
                                 {tag.imageTag}
                               </option>
                             ))}
                           </select>
                           {selectedImageTag && (
                             <button
                               onClick={handleAddSelectedTag}
                               className="bg-gray-500 text-white px-4 py-2 text-sm hover:bg-gray-600 rounded"
                             >
                               追加
                             </button>
                           )}
                         </div>
                       </div>
                     )}
                   </div>
                 )}

                 {/* Input method */}
                 {imageTagSelector === '2' && (
                   <div>
                     <label className="block text-xs text-gray-600 mb-1">タグを入力</label>
                     <div className="flex space-x-2">
                       <input
                         type="text"
                         value={inputImageTag}
                         onChange={(e) => setInputImageTag(e.target.value)}
                         className="flex-1 border border-gray-300 p-2 text-sm rounded"
                         placeholder="タグを入力"
                       />
                       <button
                         onClick={handleAddInputTag}
                         className="bg-gray-500 text-white px-4 py-2 text-sm hover:bg-gray-600 rounded"
                       >
                         追加
                       </button>
                     </div>
                   </div>
                 )}

                 {/* Search type dropdown */}
                 <div className="mt-4">
                   <label className="block text-xs text-gray-600 mb-1">検索条件</label>
                   <select
                     value={imageTagSearchType}
                     onChange={(e) => setImageTagSearchType(e.target.value)}
                     className="w-full border border-gray-300 p-2 text-sm rounded"
                   >
                     <option value="0">指定タグいずれかが設定の画像</option>
                     <option value="1">指定タグ全て設定の画像</option>
                   </select>
                 </div>

                 {/* Selected tags display */}
                 <div className="mt-4">
                   <label className="block text-xs text-gray-600 mb-1">選択されたタグ</label>
                   <div className="flex flex-wrap gap-2 min-h-[40px] p-3 border border-gray-300 rounded bg-white">
                     {(selectedImageTags || [])
                       .filter(tag => !tag.deleteFlg)
                       .map((tag, index) => (
                         <div
                           key={`${tag.imageTag}-${index}`}
                           className="bg-gray-200 border border-gray-400 inline-flex items-center text-xs px-2 py-1 rounded"
                         >
                           {tag.imageTag}
                           <button
                             onClick={() => removeImageTag(tag.imageTag)}
                             className="ml-2 text-gray-600 hover:text-gray-800"
                           >
                             <X className="w-3 h-3" />
                           </button>
                         </div>
                       ))}
                   </div>
                 </div>
               </div>
             </div>

             {/* Tablet Search buttons */}
             <div className="flex space-x-3">
               <button
                 onClick={handleSearch}
                 disabled={isLoading}
                 className="flex-1 py-3 px-6 bg-red-500 text-white text-lg font-bold hover:bg-red-600 disabled:opacity-50 disabled:cursor-not-allowed rounded"
               >
                 {isLoading ? '検索中...' : '検索'}
               </button>
               <button
                 onClick={handleClearConditions}
                 className="py-3 px-6 text-sm text-gray-500 border border-gray-400 bg-gray-100 hover:bg-gray-200 rounded"
               >
                 クリア
               </button>
             </div>
           </div>
         </div>

        {/* Pagination */}
        {totalCount > 0 && (
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            pageStart={pageStart}
            pageEnd={pageEnd}
            totalCount={totalCount}
            itemsPerPage={itemsPerPage}
            onPageChange={goToPage}
            onFirstPage={goToFirstPage}
            onLastPage={goToLastPage}
            onPreviousPage={goToPreviousPage}
            onNextPage={goToNextPage}
            onItemsPerPageChange={changeItemsPerPage}
          />
        )}



                 {/* Results Section */}
         <div className="bg-gray-100 border border-gray-300 min-h-[400px] p-2 md:p-4 lg:p-4">
           {isLoading ? (
             <div className="flex items-center justify-center h-64">
               <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-500"></div>
               <span className="ml-2 text-gray-600">検索中...</span>
             </div>
           ) : totalCount === 0 ? (
             <div className="flex items-center justify-center h-64 text-gray-500 text-center px-4">
               検索条件を入力して検索してください
             </div>
           ) : (
             <div className="flex flex-wrap gap-1 md:gap-3 lg:gap-2 justify-center md:justify-center lg:justify-start">
               {currentPageItems.map((image, index) => (
                 <ImageCard 
                   key={`${image.objectId}-${image.objectImageId}-${index}`} 
                   image={image}
                   imageService={gazoKensakuService}
                   pageType="gazoKensaku"
                 />
               ))}
             </div>
           )}
         </div>
      </div>
    </Layout>
  )
}

export default GazoKensaku
